import { format } from 'date-fns-tz'
import { useState, useEffect } from 'react'
import { kuiperClassNames, Button, Input, Conditional } from '@opallabs/kuiper'
import { validations } from '../../Content/helpers/validations'
import { styles } from './styles'

interface Props {
  url: string
  onRefreshChange: (val: string) => void
  onEmbeddedUrlChange: (val: string) => void
  onSnapshotUrlChange: (val: string) => void
  refreshedAt: string
}

export const PreviewSettings: React.FC<Props> = ({
  onRefreshChange,
  onEmbeddedUrlChange,
  onSnapshotUrlChange,
  refreshedAt,
  url,
}) => {
  const [buttonState, setButtonState] = useState('Refresh')
  const [urlInput, setUrlInput] = useState(url)
  const [isSavedFlag, setIsSavedFlag] = useState(false)
  const refreshedAtDate = new Date(refreshedAt)

  useEffect(() => {
    if (urlInput === url) {
      setButtonState('Refresh')
    } else if (validations.urlValidation.test(urlInput)) {
      setButtonState('Save')
    } else if (urlInput === '') {
      setUrlInput('http://')
      setButtonState('Disabled')
    } else {
      setButtonState('Disabled')
    }
  }, [url, urlInput])

  const onSave = () => {
    /* Handles updating retweet and snapshot url, refreshing,
     * and setting the isSavedFlag to display more relevant text
     * about when the last refresh occurred
     */
    setIsSavedFlag(true)
    setUrlInput(urlInput)
    onRefreshChange(new Date().toString())
    onEmbeddedUrlChange(urlInput)
    onSnapshotUrlChange('')
  }

  const onRefresh = () => {
    /* Handles refreshing and resetting snapshot url so as to
     * trigger the useEffect in the parent component
     */
    onRefreshChange(new Date().toString())
    onSnapshotUrlChange('snapshot_url')
  }

  const onDelete = () => {
    /* Handles refreshing and clearing retweet and snapshot url
     */
    setUrlInput('')
    onEmbeddedUrlChange('')
    onSnapshotUrlChange('')
    onRefreshChange(new Date().toString())
  }

  return (
    <div css={styles.getTooltip(buttonState !== 'Refresh')}>
      <div
        css={styles.settingsWrapper}
        className={kuiperClassNames('tweet-preview-settings')}
      >
        <h4 css={styles.titleSection}>Post Preview</h4>
        <div css={styles.inputWrapper}>
          <p>Post URL or Opal Content URL</p>
          <div css={styles.inputSection}>
            <Input
              ariaLabel="Repost URL"
              type="text"
              value={urlInput}
              style={styles.settingsInput}
              onChange={(val: string) => setUrlInput(val)}
              theme="white"
            />
            <Conditional condition={buttonState !== 'Refresh'}>
              <Button
                ariaLabel="Save"
                label="Save"
                disabled={buttonState === 'Disabled'}
                onClick={onSave}
              />
            </Conditional>
            <Conditional condition={buttonState === 'Refresh'}>
              <Button
                ariaLabel="Refresh"
                label="Refresh"
                type="secondary"
                icon="refresh"
                position="right"
                className={kuiperClassNames('refresh-button')}
                onClick={onRefresh}
              />
            </Conditional>
          </div>
          <div css={styles.refreshSection}>
            <p>
              {isSavedFlag ? 'Updated ' : 'Last Updated '}
              <span
                css={styles.refreshText}
                className={kuiperClassNames('retweet-refreshed-at')}
              >
                {format(refreshedAtDate, 'MMM d · hh:mm a zzz')}
              </span>
            </p>
          </div>
        </div>
        <div css={styles.deleteSection}>
          <Button
            ariaLabel="Delete Repost URL"
            type="invisible"
            icon="delete"
            iconOptions={{ width: 12, height: 14 }}
            onClick={onDelete}
          />
        </div>
      </div>
    </div>
  )
}
